import { useState } from "react";
import { useScrollReveal } from "@/hooks/useScrollReveal";
import type { FAQItem } from "@/mocks/faq";

interface FAQSectionProps {
  title?: string;
  items: FAQItem[];
}

function FAQRow({
  item,
  index,
  isOpen,
  onToggle,
}: {
  item: FAQItem;
  index: number;
  isOpen: boolean;
  onToggle: () => void;
}) {
  const { ref, isVisible } = useScrollReveal<HTMLDivElement>();

  return (
    <div
      ref={ref}
      className={`rounded-xl overflow-hidden transition-all duration-500 border ${
        isOpen ? "border-gold-DEFAULT/40 bg-white/8" : "border-white/8 bg-white/5"
      } ${isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-4"}`}
      style={{ transitionDelay: `${index * 50}ms` }}
    >
      <button
        onClick={onToggle}
        className="w-full flex items-center justify-between px-6 py-5 text-left cursor-pointer hover:bg-white/5 transition-colors duration-200"
        aria-expanded={isOpen}
      >
        <span className="font-sans font-semibold text-white text-base pr-6 leading-snug">
          {item.question}
        </span>
        <div className={`w-7 h-7 flex items-center justify-center rounded-full flex-shrink-0 transition-colors duration-200 ${
          isOpen ? "bg-gold-DEFAULT" : "bg-white/10"
        }`}>
          <i
            className={`text-sm transition-transform duration-300 ${
              isOpen ? "ri-subtract-line text-charcoal" : "ri-add-line text-white/70"
            }`}
          />
        </div>
      </button>
      <div
        className={`overflow-hidden transition-all duration-300 ${
          isOpen ? "max-h-80" : "max-h-0"
        }`}
      >
        <div className="px-6 pb-6 pt-0">
          <div className="h-px bg-gold-DEFAULT/20 mb-4" />
          <p className="text-white/80 font-sans text-sm leading-relaxed">
            {item.answer}
          </p>
        </div>
      </div>
    </div>
  );
}

export default function FAQSection({
  title = "Frequently Asked Questions",
  items,
}: FAQSectionProps) {
  const [openIndex, setOpenIndex] = useState<number | null>(0);
  const { ref: headerRef, isVisible: headerVisible } = useScrollReveal();

  const toggleItem = (i: number) => {
    setOpenIndex((prev) => (prev === i ? null : i));
  };

  return (
    <section
      className="bg-charcoal py-20 md:py-28"
      aria-labelledby="faq-heading"
    >
      <div className="max-w-4xl mx-auto px-6 md:px-10">
        {/* Header */}
        <div
          ref={headerRef}
          className={`text-center mb-12 transition-all duration-700 ${
            headerVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-6"
          }`}
        >
          <div className="inline-flex items-center gap-3 mb-5 justify-center">
            <span className="h-px w-10 bg-gold-DEFAULT" />
            <span className="text-gold-DEFAULT text-xs font-bold tracking-[0.22em] uppercase font-sans">
              VOICE SEARCH READY
            </span>
            <span className="h-px w-10 bg-gold-DEFAULT" />
          </div>
          <h2
            id="faq-heading"
            className="font-serif text-white leading-tight max-w-2xl mx-auto"
            style={{ fontSize: "clamp(1.9rem, 3vw, 2.7rem)" }}
          >
            {title}
          </h2>
          <p className="text-white/90 font-sans text-sm mt-4 max-w-md mx-auto">
            Natural language answers for voice search and AI assistants — ask Siri, Alexa, or Google too.
          </p>
        </div>

        {/* FAQ Items */}
        <div className="flex flex-col gap-2.5">
          {items.map((item, i) => (
            <FAQRow
              key={i}
              item={item}
              index={i}
              isOpen={openIndex === i}
              onToggle={() => toggleItem(i)}
            />
          ))}
        </div>

        {/* CTA below FAQ */}
        <div className="mt-12 text-center">
          <p className="text-white/90 font-sans text-sm mb-6">
            Still have questions? Call or email us — we respond fast.
          </p>
          <div className="flex flex-col sm:flex-row items-center justify-center gap-3">
            <a
              href="tel:9527693208"
              className="inline-flex items-center gap-2.5 bg-gold-DEFAULT text-charcoal font-bold text-sm px-8 py-4 rounded-full hover:bg-gold-light transition-colors duration-200 whitespace-nowrap cursor-pointer tracking-wide"
              aria-label="Call United Auto Group (952) 769-3208"
            >
              <div className="w-4 h-4 flex items-center justify-center">
                <i className="ri-phone-fill text-sm" />
              </div>
              Call (952) 769-3208
            </a>
            <a
              href="mailto:info@uagmn.com"
              className="inline-flex items-center gap-2 border border-white/20 text-white font-semibold text-sm px-8 py-4 rounded-full hover:border-white/40 hover:text-white transition-all duration-200 whitespace-nowrap cursor-pointer"
            >
              <div className="w-4 h-4 flex items-center justify-center">
                <i className="ri-mail-line text-sm" />
              </div>
              info@uagmn.com
            </a>
          </div>
        </div>
      </div>
    </section>
  );
}
